'use client';

import i18next from 'i18next';
import { useEffect } from 'react';
import {
  initReactI18next,
  useTranslation as useTranslationOrg,
} from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { fallbackLng, getOptions, languages } from './settings';
import { useNavigate, useParams } from 'react-router-dom';

// Get browser language
function getBrowserLanguage(): string {
  if (typeof window === 'undefined') return fallbackLng;

  const browserLang = window.navigator.language.split('-')[0];
  return languages.includes(browserLang) ? browserLang : fallbackLng;
}

// Custom language detector that uses URL path or browser language
const languageDetector = {
  name: 'languageDetector',
  lookup() {
    if (typeof window === 'undefined') return fallbackLng;

    // First try to get language from URL
    const path = window.location.pathname;
    const langMatch = path.match(/^\/([^\/]+)/);
    const urlLang = langMatch ? langMatch[1] : null;

    if (urlLang && languages.includes(urlLang)) {
      return urlLang;
    }

    // If no valid language in URL, use browser language
    return getBrowserLanguage();
  },
  cacheUserLanguage() {
    // No caching needed
  },
};

// Update document direction based on language
function updateDocumentDirection(lang: string) {
  if (typeof window === 'undefined') return;
  const rtlLanguages = ['ar', 'he'];
  document.documentElement.dir = rtlLanguages.includes(lang) ? 'rtl' : 'ltr';
}

// on client side the normal singleton is ok
i18next
  .use(initReactI18next)
  .use({
    type: 'languageDetector',
    async: false,
    init: () => {},
    detect: languageDetector.lookup,
    cacheUserLanguage: languageDetector.cacheUserLanguage,
  })
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./locales/${language}/${namespace}.json`),
    ),
  )
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['languageDetector'],
    },
  });

export function useTranslation(ll: string, ns?: string, options?: object) {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const browserLang = getBrowserLanguage();

  // Determine which language to use
  const detectedLang = lang && languages.includes(lang) ? lang : browserLang;

  const ret = useTranslationOrg(ns, options);
  const { i18n } = ret;

  useEffect(() => {
    // If no language in URL and we're not already on the browser language path
    if (!lang && window.location.pathname !== `/${browserLang}`) {
      // Redirect to browser language path
      navigate(`/${browserLang}${window.location.pathname}`);
      return;
    }

    // Update i18n language if needed
    if (i18n.resolvedLanguage !== detectedLang) {
      i18n.changeLanguage(detectedLang);
      updateDocumentDirection(detectedLang);
    }
  }, [lang, browserLang, detectedLang, i18n, navigate]);

  return ret;
}
